/**
 * THE PRO LIBRARY — what a signed-in pro can open, and how the link is minted.
 *
 * Peter, 2026-08-26, on IMAGE corporate's Drive folder: "some of those forms or
 * document may be better served inside the portal because it's more accessible
 * now that the pros are gonna be there… The permission they have comes through
 * the application."
 *
 * So the files live in the PRIVATE `pro-library` bucket (backend migration
 * 20260903000000) and are reached only through short-lived signed URLs, the
 * same way compliance-docs and maintenance-photos already work. Nothing here is
 * fetchable by guessing a URL: proven against production before this shipped —
 * anonymous is refused, a pro gets their own studio's folder and corporate's
 * shared one, and ANOTHER studio's folder answers 404.
 *
 * WHY THE HANDBOOK IS A PATH AND NOT A LINK. A signed URL expires, so it can
 * never be stored in My Studio. The platform-rendered per-studio handbook lives
 * at a CONVENTION — `<location_id>/image-pro-handbook.pdf` — and the page signs
 * it at view time. My Studio still governs: a studio that hosts its own
 * handbook elsewhere sets handbook_url and that wins (see Documents.tsx).
 *
 * THE COURSES ARE BEHIND THIS DOOR TOO (Peter, 2026-08-26: "put the courses
 * behind the same door"). They were briefly public R2 objects — his earlier
 * "stream it from r2" — and the public copies were deleted when they moved, so
 * the library has exactly one home and one rule. Seeking still works: a signed
 * Supabase URL answers a range request with 206 and a correct content-range,
 * measured on the 275MB video before this shipped, so a pro can scrub.
 */
import { supabase } from '@/lib/supabase';
import { isCell } from '@/lib/cell/config';
import { cellSignMany } from '@/lib/cell/files';

export const PRO_LIBRARY_BUCKET = 'pro-library';

/** The per-studio handbook's conventional path inside the bucket. */
export const handbookPath = (locationId: string) => `${locationId}/image-pro-handbook.pdf`;

export type LibraryKind = 'read' | 'download' | 'watch' | 'listen';

export interface LibraryItem {
  title: string;
  description: string;
  kind: LibraryKind;
  /** Path inside pro-library — signed at view time. */
  path?: string;
  /** Public media URL — used only by the course media (see the header). */
  url?: string;
}

/**
 * IMAGE corporate's shared library, mirroring the Drive folder's own structure
 * (Peter: "I would also keep the structure of the folder when it comes over to
 * us"). Every path below was uploaded from that folder; the two owner-facing
 * items in it — the onboarding deck for OWNERS and an archived subfolder — are
 * deliberately absent, because this shelf is the pros'.
 */
export const IMAGE_LIBRARY: ReadonlyArray<{ group: string; items: LibraryItem[] }> = [
  {
    group: 'Guides',
    items: [
      {
        title: 'IPA Business Manual',
        description: 'The IMAGE Pro Academy business manual — pricing, clients, growth, the whole playbook.',
        kind: 'read',
        path: 'image/guides/ipa-business-manual.pdf',
      },
      {
        title: 'IMAGE Studios Style Guide',
        description: 'Brand colours, logos and how to use them when you post about your studio.',
        kind: 'read',
        path: 'image/guides/image-studios-style-guide.pdf',
      },
    ],
  },
  {
    group: 'Worksheets & forms',
    items: [
      {
        title: 'Raising Prices Calculator',
        description: 'Work out what a price change does to your take-home before you make it.',
        kind: 'download',
        path: 'image/worksheets-and-forms/raising-prices-calculator.xlsx',
      },
      {
        title: 'Pro Forma / Break-Even Worksheet',
        description: 'What you need to book each week to cover rent and supplies.',
        kind: 'download',
        path: 'image/worksheets-and-forms/proforma-break-even-worksheet.xlsx',
      },
      {
        title: 'Price-Increase Letter Template',
        description: 'The letter to send your clients — edit the words, keep the tone.',
        kind: 'download',
        path: 'image/worksheets-and-forms/price-increase-letter-template.docx',
      },
      {
        title: 'Retirement Planning Calculator',
        description: 'Compound interest on what you set aside, year by year.',
        kind: 'download',
        path: 'image/worksheets-and-forms/retirement-compound-interest-calculator.xlsx',
      },
      {
        title: 'Waste Worksheet',
        description: 'Where product money leaks — measure it, then cut it.',
        kind: 'download',
        path: 'image/worksheets-and-forms/waste-worksheet.xlsx',
      },
      {
        title: 'Personal Monthly Budget',
        description: 'Your own money, month by month — separate from the business.',
        kind: 'download',
        path: 'image/worksheets-and-forms/personal-monthly-budget.xlsx',
      },
      {
        title: '45-Day Marketing Plan',
        description: 'A six-week plan you fill in once and follow.',
        kind: 'download',
        path: 'image/worksheets-and-forms/45-day-marketing-plan.xlsx',
      },
    ],
  },
  {
    group: 'Courses',
    items: [
      {
        title: 'Suite Success — Pro Onboarding',
        description: 'The IMAGE Pro onboarding session. Watch it whenever you like.',
        kind: 'watch',
        path: 'image/courses/suite-success.mp4',
      },
      {
        title: 'Pro Wellness — Week 1',
        description: 'The first wellness session, to listen to between clients.',
        kind: 'listen',
        path: 'image/courses/pro-wellness-week-1.m4a',
      },
      {
        title: 'Pro Wellness — Week 2',
        description: 'The second wellness session.',
        kind: 'listen',
        path: 'image/courses/pro-wellness-week-2.m4a',
      },
    ],
  },
];

/**
 * PER-STUDIO TALKS — content that belongs to ONE studio's pros, keyed by location.
 *
 * Peter recorded "AI for Pros" for the IMAGE Studios Sharon pros; it is theirs, not
 * the whole network's. So it lives here keyed on `sharon_zoho`, and the Documents
 * shelf renders a talk ONLY for the studio it is keyed to — every other studio's
 * pros get nothing (no group), exactly like the handbook is per-studio. The file
 * sits in this same private bucket at `<location_id>/talks/…` and is signed at view
 * time; nothing is public. To give another studio a talk, add a row — no code path
 * changes, the gate is the key.
 */
export const STUDIO_TALKS: Record<string, LibraryItem[]> = {
  sharon_zoho: [
    {
      title: 'AI for Pros — a talk from Peter',
      description:
        'A plain-English walk through what AI really is in 2026 and the practical, money-making ways to put it to work this month — marketing, content, sales, and staying in touch with clients. About 11 minutes.',
      kind: 'watch',
      path: 'sharon_zoho/talks/ai-for-pros.mp4',
    },
  ],
};

/** The talks THIS studio's pros should see — empty for a studio with none. */
export function talksFor(locationId: string | null): LibraryItem[] {
  return locationId ? STUDIO_TALKS[locationId] ?? [] : [];
}

/** Every bucket path the shelf can show, including the studio's own handbook. */
export function libraryPaths(locationId: string | null, studioDocPaths: string[] = []): string[] {
  const shared = IMAGE_LIBRARY.flatMap((g) => g.items.map((i) => i.path).filter(Boolean) as string[]);
  // This studio's own talks are signed in the same batch as everything else.
  const talks = talksFor(locationId).map((t) => t.path).filter(Boolean) as string[];
  // The studio's OWN uploads are signed in the same batch — one round trip for
  // the whole shelf, whoever put the document there.
  return locationId ? [handbookPath(locationId), ...studioDocPaths, ...talks, ...shared] : shared;
}

/**
 * Mint the links, one batch, one round trip.
 *
 * Signed in bulk on load rather than per click: a click handler that awaits
 * before opening a window is what popup blockers eat, and a plain <a href> is
 * also what makes `download` and a video player work normally.
 *
 * A path the caller may not read (another studio's folder) simply comes back
 * without a URL — the shelf then omits it rather than rendering a dead card.
 */
export async function signLibrary(paths: string[], expiresIn = 3600): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  // CELL (M28): POST /v1/pro-hub/files/sign — the same batch, each path checked by the pro-library rule (same shape).
  const { data, error } = isCell()
    ? await cellSignMany(PRO_LIBRARY_BUCKET, paths, expiresIn)
    : await supabase.storage
      .from(PRO_LIBRARY_BUCKET)
      .createSignedUrls(paths, expiresIn);
  // HUB-071: a failed batch is a FAILURE, never an empty library — it used to return {} and the page
  // then told the pro "Your owner hasn't added anything yet". The caller renders a retry.
  if (error) {
    console.error('pro library signing failed', error);
    throw new Error('Your documents could not be opened just now.');
  }
  const out: Record<string, string> = {};
  for (const row of data ?? []) {
    // supabase-js returns { path, signedUrl, error } per item — a per-item
    // error means "not yours" (or not there), which is a normal answer for the
    // shared shelf and an absent handbook. For a file the OWNER added it is not:
    // Documents.tsx names those (HUB-071, unopenedSentence) instead of hiding them.
    if (row.path && row.signedUrl && !row.error) out[row.path] = row.signedUrl;
  }
  return out;
}
